"use client";

import {
  type ReactNode,
  type RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import {
  Button,
  Card,
  Container,
  EnergyLabel,
  Flex,
  Message,
  Placeholder,
  ProgressBar,
  Select,
  Spacing,
  Text,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";
import { mergeClasses, rem } from "@huub-web/utils";

import {
  type DebtMarketValue,
  debtMarketValueOptions,
  type MortgageInterestRate,
  type MortgageInterestRatePeriod,
  mortgageInterestRatePeriodOptions,
} from "../../types/MortgageInterestRate";
import { InfoIcon } from "../InfoIcon";

import { energyLabels, type EnergyLabelValue } from "../../types";
import {
  energyLabelSelectStyle,
  energyLabelStyle,
  filterGridSecondColumnStyle,
  filterGridThirdColumnStyle,
  filterGridStyle,
  rentevastePeriodSelectStyle,
  subGridStyle,
  listStyle,
  filterGridFirstColumnStyle,
  debtMarketValueOptionsContainerStyle,
  filterOptionsContainerStyle,
  textStyle,
} from "./InterestRatesOverview.css";
import { InterestRateRow } from "./components/InterestRateRow";
import { Section, type SectionProps } from "../Section";

export type InterestRatesOverviewFilterValues = {
  rentevastePeriod: MortgageInterestRatePeriod;
  debtMarketValue: DebtMarketValue;
  energyLabel: EnergyLabelValue;
};

export type InterestRatesOverviewHandle = {
  /**
   * Imperatively update filter values from outside the component.
   */
  updateFilterValues: (
    values: Partial<InterestRatesOverviewFilterValues>,
  ) => void;
};

export type InterestRatesOverviewProps = {
  /**
   * Call-to-action element displayed in the overview
   */
  cta?: ReactNode;
  /**
   * Compact call-to-action element for smaller viewports
   */
  ctaSmall?: ReactNode;
  /**
   * Call-to-action element displayed at the start of the overview
   */
  startCTA?: ReactNode;
  /**
   * Maximum number of interest rates to display
   */
  limit?: number;
  /**
   * Callback to fetch interest rates, optionally filtered by bank name
   */
  getInterestRates: (
    period: MortgageInterestRatePeriod,
    debtMarketValue: DebtMarketValue,
    energyLabel: EnergyLabelValue,
    limit: number,
    bank?: string,
  ) => Promise<MortgageInterestRate[]>;
  /**
   * Callback fired when the loading state changes
   */
  onLoadingChange?: (loading: boolean) => void;
  /**
   * Bank name to filter interest rates by
   */
  bank?: string;
  /**
   * Generates a bank-specific page href from a bank identifier (slug or name)
   */
  getBankHref?: (bankSlug: string) => string;
  /**
   * Generates a bank image element from source URL, bank name, optional href, and optional slug
   */
  getBankImage?: (
    src: string,
    name: string,
    href?: string,
    slug?: string,
  ) => ReactNode;
  /**
   * Edge style applied to the wrapping Section
   */
  edge?: SectionProps["edge"];
  /**
   * Position of the edge within the wrapping Section
   */
  edgePosition?: SectionProps["edgePosition"];
  /**
   * Visual variant of the overview
   */
  variant?: "app" | "marketing";
  /**
   * Title for the gradient CTA card (falls back to translation)
   */
  ctaTitle?: string;
  /**
   * Description for the gradient CTA card (falls back to translation)
   */
  ctaDescription?: string;
  /**
   * Callback fired after interest rates are successfully loaded with the
   * current filter values.
   */
  onFiltersLoaded?: (filters: {
    rentevastePeriod: string;
    debtMarketValue: string;
    energyLabel: string;
  }) => void;
  /**
   * Ref to imperatively update filter values from a parent template.
   */
  filterRef?: RefObject<InterestRatesOverviewHandle | null>;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * InterestOverview component displays an overview of mortgage interest rates.
 */
export const InterestRatesOverview: FC<InterestRatesOverviewProps> = ({
  cta,
  ctaSmall,
  startCTA,
  getInterestRates,
  onLoadingChange,
  limit = 100,
  bank,
  getBankHref,
  getBankImage,
  edge,
  edgePosition = "top",
  variant = "marketing",
  ctaTitle,
  ctaDescription,
  onFiltersLoaded,
  filterRef,
  ref,
}) => {
  const { t } = useTranslation();
  const onFiltersLoadedRef = useRef(onFiltersLoaded);
  onFiltersLoadedRef.current = onFiltersLoaded;
  const lastReportedFiltersRef = useRef<string>("");

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [rentevastePeriod, setRentevastePeriod] =
    useState<MortgageInterestRatePeriod>("20");
  const [debtMarketValue, setDebtMarketValue] =
    useState<DebtMarketValue>("nhg");
  const [energyLabel, setEnergyLabel] = useState<EnergyLabelValue>("A");

  const [interestRates, setInterestRates] = useState<MortgageInterestRate[]>(
    [],
  );

  useImperativeHandle(
    filterRef,
    () => ({
      updateFilterValues: (values) => {
        if (values.rentevastePeriod !== undefined) {
          setRentevastePeriod(values.rentevastePeriod);
        }
        if (values.debtMarketValue !== undefined) {
          setDebtMarketValue(values.debtMarketValue);
        }
        if (values.energyLabel !== undefined) {
          setEnergyLabel(values.energyLabel);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    setLoading(true);
    setError(false);

    getInterestRates(
      rentevastePeriod,
      debtMarketValue,
      energyLabel,
      limit,
      bank,
    )
      .then((rates) => {
        setInterestRates(rates.slice(0, limit));
        const filterKey = JSON.stringify({
          rentevastePeriod,
          debtMarketValue,
          energyLabel,
        });
        if (filterKey !== lastReportedFiltersRef.current) {
          lastReportedFiltersRef.current = filterKey;
          onFiltersLoadedRef.current?.({
            rentevastePeriod,
            debtMarketValue,
            energyLabel,
          });
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [
    rentevastePeriod,
    debtMarketValue,
    energyLabel,
    limit,
    bank,
    getInterestRates,
  ]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const debtMarketValueLabel = debtMarketValueOptions.find(
    (option) => option.value === debtMarketValue,
  )?.label;

  const handleDebtMarketValueChange = (value: DebtMarketValue) => {
    setDebtMarketValue(value);
  };

  const renteVastOptions = mortgageInterestRatePeriodOptions.map((option) => ({
    label: t("interestRatesOverview.year", {
      count: Number(option.label),
    }),
    value: option.value,
  }));

  const firstRates = interestRates.slice(0, 6);
  const remainingRates = interestRates.slice(6);

  return (
    <Section ref={ref} overflowVisible edge={edge} edgePosition={edgePosition}>
      <div className={filterOptionsContainerStyle}>
        <Container size="marketing">
          <Card
            contentClassName={mergeClasses(filterGridStyle)}
            variant="tertiary"
            border={variant === "marketing" ? "default" : "none"}
          >
            <Flex
              direction="column"
              gap="s"
              className={filterGridFirstColumnStyle}
            >
              <Text as="label" bold htmlFor="rentevastePeriod" noWrap>
                {t(
                  "maxMortgageWizard.result.interestRates.fixedRatePeriod.label",
                )}{" "}
                <InfoIcon
                  value={t(
                    "maxMortgageWizard.result.interestRates.fixedRatePeriod.info.singular",
                  )}
                />
              </Text>
              <Select
                value={rentevastePeriod}
                disabled={loading || !!error}
                onChange={(value) =>
                  setRentevastePeriod(value as MortgageInterestRatePeriod)
                }
                placeholder={t(
                  "interestRatesOverview.fixedRatePeriodPlaceholder",
                )}
                options={renteVastOptions}
                className={rentevastePeriodSelectStyle}
                id="rentevastePeriod"
              />
            </Flex>
            <Flex
              direction="column"
              gap="s"
              className={mergeClasses(
                filterGridSecondColumnStyle,
                debtMarketValueOptionsContainerStyle,
              )}
            >
              <Text bold noWrap>
                {t("interestRatesOverview.ltvLabel")}{" "}
                <InfoIcon value={t("interestRatesOverview.ltvInfo")} />
              </Text>
              <div className={subGridStyle}>
                {debtMarketValueOptions.map((option) => (
                  <Button
                    key={option.value}
                    onClick={() => handleDebtMarketValueChange(option.value)}
                    active={debtMarketValue === option.value}
                    variant="background"
                    border="default"
                    align="center"
                    disabled={loading || !!error}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </Flex>
            <Flex
              direction="column"
              gap="s"
              className={filterGridThirdColumnStyle}
              align="flex-end"
            >
              <Text as="label" bold htmlFor="energyLabel" noWrap>
                {t("interestRatesOverview.energyLabel")}{" "}
                <InfoIcon value={t("interestRatesOverview.energyLabelInfo")} />
              </Text>
              <Select
                value={energyLabel}
                disabled={loading || !!error}
                onChange={(value) => setEnergyLabel(value as EnergyLabelValue)}
                placeholder={t("interestRatesOverview.energyLabelPlaceholder")}
                options={energyLabels.map((label) => ({
                  label: (
                    <EnergyLabel
                      label={label}
                      size="s"
                      className={energyLabelStyle}
                    />
                  ),
                  value: label,
                }))}
                className={energyLabelSelectStyle}
                name={t("interestRatesOverview.energyLabelName")}
                id="energyLabel"
              />
            </Flex>
          </Card>
        </Container>
      </div>
      <Container size="marketing">
        <Card
          elevated={variant === "marketing"}
          leading={
            loading && <ProgressBar variant="infinite" position="absolute" />
          }
          overflowHidden
          border={variant === "app" ? "default" : "none"}
        >
          <Text bold variant="displayM">
            {t("interestRatesOverview.heading", {
              period: rentevastePeriod,
              debtMarketValue: debtMarketValueLabel,
            })}
          </Text>
          {loading && interestRates.length === 0 && (
            <Flex gap="s" direction="column">
              <Spacing />
              <Placeholder height={rem(64)} />
              <Placeholder height={rem(64)} />
              <Placeholder height={rem(64)} />
            </Flex>
          )}
          {error && !loading && (
            <>
              <Spacing />
              <Message
                variant="error"
                body={t("interestRatesOverview.error")}
              />
            </>
          )}
          {!error && interestRates && interestRates.length > 0 && (
            <>
              <Flex direction="column" className={listStyle}>
                {firstRates.map((interestRate) => (
                  <InterestRateRow
                    key={interestRate.id}
                    interestRate={interestRate}
                    debtMarketValue={debtMarketValue}
                    cta={cta}
                    ctaSmall={ctaSmall}
                    image={
                      getBankImage
                        ? getBankImage(
                            interestRate.image,
                            interestRate.name,
                            getBankHref?.(
                              interestRate.slug ?? interestRate.name,
                            ),
                            interestRate.slug,
                          )
                        : undefined
                    }
                  />
                ))}
              </Flex>

              <Card variant="gradient">
                <Spacing height="xxs" />
                <Text as="h3" variant="displayL" bold align="center">
                  {ctaTitle ?? t("interestRatesOverview.ctaTitle")}
                </Text>
                <Text as="p" align="center" className={mergeClasses(textStyle)}>
                  {ctaDescription ?? t("interestRatesOverview.ctaSubtitle")}
                </Text>
                {startCTA}
                <Spacing height="xxs" />
              </Card>

              {remainingRates.length > 0 && (
                <Flex direction="column" className={listStyle}>
                  {remainingRates.map((interestRate) => (
                    <InterestRateRow
                      key={interestRate.id}
                      interestRate={interestRate}
                      debtMarketValue={debtMarketValue}
                      cta={cta}
                      ctaSmall={ctaSmall}
                      image={
                        getBankImage
                          ? getBankImage(
                              interestRate.image,
                              interestRate.name,
                              getBankHref?.(
                                interestRate.slug ?? interestRate.name,
                              ),
                              interestRate.slug,
                            )
                          : undefined
                      }
                    />
                  ))}
                </Flex>
              )}
            </>
          )}
        </Card>
      </Container>
      <Spacing />
    </Section>
  );
};
